import PropTypes from 'prop-types';
import WebsiteIcon from '../icons/website.svg';
import TelegramIcon from '../icons/telegram.svg';
import TwitterIcon from '../icons/x-icon.svg';

// Format large numbers with suffixes (K, M, B, T)
const formatNumber = (num) => {
  if (!num) return '0';
  if (num >= 1e12) return (num / 1e12).toFixed(2) + 'T';
  if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
  return num.toFixed(2);
};

const SocialSection = ({ website, telegram, twitter }) => (
  <div className="flex justify-center gap-4 mt-4">
    {twitter && (
      <a href={twitter} target="_blank" rel="noopener noreferrer">
        <img src={TwitterIcon} alt="Twitter" className="w-5 h-5" style={{ filter: 'var(--icon-filter)' }} />
      </a>
    )}
    {telegram && (
      <a href={telegram} target="_blank" rel="noopener noreferrer">
        <img src={TelegramIcon} alt="Telegram" className="w-5 h-5" style={{ filter: 'var(--icon-filter)' }} />
      </a>
    )}
    {website && (
      <a href={website} target="_blank" rel="noopener noreferrer">
        <img src={WebsiteIcon} alt="Website" className="w-5 h-5" style={{ filter: 'var(--icon-filter)' }} />
      </a>
    )}
  </div>
);

const PumpInfoSection = ({ marketCap, tokenSupplyUSD, volume }) => (
  <div className="grid grid-cols-3 gap-4 mt-4">
    <div className="flex flex-col items-center">
      <span className="text-xs" style={{ color: 'var(--text-primary)' }}>MCAP</span>
      <span className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
        ${formatNumber(marketCap + 4000)}
      </span>
    </div>
    <div className="flex flex-col items-center">
      <span className="text-xs" style={{ color: 'var(--text-primary)' }}>TVL</span>
      <span className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
        ${formatNumber(tokenSupplyUSD)}
      </span>
    </div>
    <div className="flex flex-col items-center">
      <span className="text-xs" style={{ color: 'var(--text-primary)' }}>Volume</span>
      <span className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
        ${formatNumber(volume / 10**18)}
      </span>
    </div>
  </div>
);

const ClaimCard = ({
  tokenName,
  banner,
  maxLimit,
  tokenSymbol,
  tokenSupplyUSD,
  volume,
  website,
  telegram,
  twitter,
  description,
  ethPrice,
  lpCreated,
  tokenPrice
}) => {
  const marketCap = (tokenPrice * 10 ** 9 * Number(ethPrice)) / 10 ** 12;
  let maxLimitAmount = maxLimit * 10 ** 9 / 100;
  let progress = (marketCap / 50000) * 100;
  if (lpCreated) {
    progress = 100
  }

  return (
    <div className="rounded-xl overflow-hidden" style={{
      background: 'var(--card-bg)',
      border: '1px solid transparent',
      backgroundImage: 'linear-gradient(var(--card-bg), var(--card-bg)), var(--card-border)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      boxShadow: 'var(--shadow)'
    }}>
      <div className="p-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center gap-2">
            {banner}
            <div className='flex justify-center items-center gap-4'>
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{tokenName}</h2>
              <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{tokenSymbol}</p>
            </div>
          </div>

          <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
            {description}
          </p>

          <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
            Max Limit: {maxLimit}% ({maxLimitAmount} {tokenSymbol})
          </div>

          <div className="mt-4">
            <div className="flex justify-between text-xs mb-1">
              <span style={{ color: 'var(--text-primary)' }}>Bonding Curve Progress</span>
              <span style={{ color: 'var(--text-accent)' }}>{(progress.toFixed(2))}%</span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--progress-container-bg)' }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, progress)}%`,
                  background: 'var(--progress-fill)'
                }}
              />
            </div>
          </div>

          <SocialSection website={website} telegram={telegram} twitter={twitter} />
          <PumpInfoSection
            marketCap={marketCap}
            tokenSupplyUSD={tokenSupplyUSD}
            volume={volume}
          />
        </div>
      </div>
    </div>
  );
};

ClaimCard.propTypes = {
  tokenName: PropTypes.string.isRequired,
  Logo: PropTypes.element.isRequired,
  tokenAddress: PropTypes.string.isRequired,
  contractAddress: PropTypes.string.isRequired,
  dexAddress: PropTypes.string.isRequired,
  devAddress: PropTypes.string.isRequired,
  dexName: PropTypes.string.isRequired,
  tokenSymbol: PropTypes.string.isRequired,
  tokenDecimals: PropTypes.number.isRequired,
  tokenTotalSupply: PropTypes.number.isRequired,
  tokenSupplyUSD: PropTypes.number.isRequired,
  tokenUnsoldTokens: PropTypes.string.isRequired,
  tokenCover: PropTypes.string.isRequired,
  website: PropTypes.string,
  telegram: PropTypes.string,
  twitter: PropTypes.string,
  banner: PropTypes.element.isRequired,
};

export default ClaimCard;
